import {
  BALANCE,
  BATCH_TRANSACTIONS,
  CHARGES,
  DASHBOARD,
  DENIED_ENTRIES,
  DIETARY_BOOKINGS,
  DIETARY_OPTIONS,
  DIETARY_TOTALS,
  ENTRIES,
  ENTRIES_TOTALS,
  FEEDBACK,
  FEES,
  HOLIDAYS,
  LIMITS,
  MEAL_BOOKING_SETTINGS,
  MEAL_BOOKINGS,
  MEAL_BOOKINGS_TOTALS,
  MEAL_MENUS,
  MEAL_OPTIONS,
  MEAL_PLAN_INTEGRITY_ERRORS,
  MEAL_PLANS,
  MENU_ITEM_CATEGORIES,
  MENU_ITEMS,
  MENU_STRUCTURES,
  MENU_TEMPLATES,
  NOTIFICATIONS,
  ORDERS,
  PROFILE,
  RESIDENCES,
  SEMESTER_MENUS,
  SURVEYS,
  SYSTEM,
  TERM_HOLIDAYS,
  TIMES,
  TRANSACTIONS,
  UPCOMING_MEALS,
  USERS,
} from "constants/routes";
import { lazy } from "react";
import { componentLoader } from "utils/utils";

const Dashboard = lazy(() =>
  componentLoader(() => import("views/Dashboard/Dashboard")),
);
const Profile = lazy(() =>
  componentLoader(() => import("views/Users/UserProfile")),
);
const Transactions = lazy(() =>
  componentLoader(() => import("views/Transactions/Transactions")),
);
const Users = lazy(() => componentLoader(() => import("views/Users/Users")));
const User = lazy(() => componentLoader(() => import("views/Users/User")));

const Holidays = lazy(() =>
  componentLoader(() => import("views/Holidays/Holidays")),
);
const TermHolidays = lazy(() =>
  componentLoader(() => import("views/TermHolidays/TermHolidays")),
);
const Residences = lazy(() =>
  componentLoader(() => import("views/Residences/Residences")),
);
const Fees = lazy(() => componentLoader(() => import("views/Fees/Fees")));
const Charges = lazy(() =>
  componentLoader(() => import("views/Charges/Charges")),
);
const MealPeriods = lazy(() =>
  componentLoader(() => import("views/MealPeriods/MealPeriods")),
);
const DietaryOptions = lazy(() =>
  componentLoader(() => import("views/DietaryOptions/DietaryOptions")),
);
const MenuItemCategories = lazy(() =>
  componentLoader(() => import("views/MenuItemCategories/MenuItemCategories")),
);
const MenuItems = lazy(() =>
  componentLoader(() => import("views/MenuItems/MenuItems")),
);
const MenuTemplates = lazy(() =>
  componentLoader(() => import("views/MenuTemplates/MenuTemplates")),
);
const MenuStructures = lazy(() =>
  componentLoader(() => import("views/MenuStructures/MenuStructures")),
);
const SemesterMenus = lazy(() =>
  componentLoader(() => import("views/SemesterMenus/SemesterMenus")),
);
const SemesterMenu = lazy(() =>
  componentLoader(() => import("views/SemesterMenus/SemesterMenu")),
);
const UpcomingMeals = lazy(() =>
  componentLoader(() => import("views/UpcomingMeals/UpcomingMeals")),
);
const DietaryBookings = lazy(() =>
  componentLoader(() => import("views/DietaryBookings/DietaryBookings")),
);
const DietaryBookingsTotals = lazy(() =>
  componentLoader(
    () => import("views/DietaryBookingsTotals/DietaryBookingsTotals"),
  ),
);
const MealBookings = lazy(() =>
  componentLoader(() => import("views/MealBookings/MealBookings")),
);
const MealBookingsTotals = lazy(() =>
  componentLoader(() => import("views/MealBookingsTotals/MealBookingsTotals")),
);
const SystemSettings = lazy(() =>
  componentLoader(() => import("views/Settings/SystemSettings")),
);
const BalanceSettings = lazy(() =>
  componentLoader(() => import("views/Settings/BalanceSettings")),
);
const MealSettings = lazy(() =>
  componentLoader(() => import("views/Settings/MealSettings")),
);
const MealPlans = lazy(() =>
  componentLoader(() => import("views/MealPlans/MealPlans")),
);
const MealPlanIntegrityErrors = lazy(() =>
  componentLoader(() => import("views/MealPlans/MealPlanIntegrityErrors")),
);

const Limits = lazy(() => componentLoader(() => import("views/Limits/Limits")));
const Entries = lazy(() =>
  componentLoader(() => import("views/Entries/Entries")),
);
const EntriesTotals = lazy(() =>
  componentLoader(() => import("views/EntriesTotals/EntriesTotals")),
);
const DeniedEntries = lazy(() =>
  componentLoader(() => import("views/Entries/DeniedEntries")),
);
const Orders = lazy(() => componentLoader(() => import("views/Orders/Orders")));
const BatchTransactions = lazy(() =>
  componentLoader(() => import("views/BatchTransactions/BatchTransactions")),
);
const Notifications = lazy(() =>
  componentLoader(() => import("views/Notifications/Notifications")),
);

const Feedback = lazy(() =>
  componentLoader(() => import("views/Feedback/Feedback")),
);
const Surveys = lazy(() =>
  componentLoader(() => import("views/Surveys/Surveys")),
);
const Survey = lazy(() =>
  componentLoader(() => import("views/Surveys/Survey")),
);
const SurveyReporting = lazy(() =>
  componentLoader(() => import("views/Surveys/SurveyReporting")),
);

const routes = [
  {
    path: DASHBOARD,
    component: Dashboard,
  },
  {
    path: PROFILE,
    component: Profile,
  },
  {
    path: MEAL_PLANS,
    component: MealPlans,
  },
  {
    path: MEAL_PLAN_INTEGRITY_ERRORS,
    component: MealPlanIntegrityErrors,
  },
  {
    path: FEES,
    component: Fees,
  },
  {
    path: BALANCE,
    component: BalanceSettings,
  },
  {
    path: MEAL_BOOKING_SETTINGS,
    component: MealSettings,
  },
  {
    path: TRANSACTIONS,
    component: Transactions,
  },
  { path: USERS, component: Users },
  {
    path: USERS + "/:identifier",
    component: User,
  },
  {
    path: ENTRIES,
    component: Entries,
  },
  {
    path: ENTRIES_TOTALS,
    component: EntriesTotals,
  },
  {
    path: DENIED_ENTRIES,
    component: DeniedEntries,
  },
  {
    path: BATCH_TRANSACTIONS,
    component: BatchTransactions,
  },
  {
    path: NOTIFICATIONS,
    component: Notifications,
  },
  {
    path: FEEDBACK,
    component: Feedback,
  },
  {
    path: SURVEYS,
    component: Surveys,
  },
  {
    path: SURVEYS + "/:id",
    component: Survey,
  },
  {
    path: SURVEYS + "/:id/reporting",
    component: SurveyReporting,
  },
  {
    path: HOLIDAYS,
    component: Holidays,
  },
  {
    path: TERM_HOLIDAYS,
    component: TermHolidays,
  },
  {
    path: LIMITS,
    component: Limits,
  },
  {
    path: CHARGES,
    component: Charges,
  },
  {
    path: RESIDENCES,
    component: Residences,
  },
  {
    path: SYSTEM,
    component: SystemSettings,
  },
  {
    path: TIMES,
    component: MealPeriods,
  },
  {
    path: DIETARY_OPTIONS,
    component: DietaryOptions,
  },
  {
    path: MEAL_OPTIONS,
    component: MenuItems,
  },
  {
    path: MEAL_MENUS,
    component: MenuTemplates,
  },
  {
    path: MENU_ITEM_CATEGORIES,
    component: MenuItemCategories,
  },
  {
    path: MENU_ITEMS,
    component: MenuItems,
  },
  {
    path: MENU_TEMPLATES,
    component: MenuTemplates,
  },
  {
    path: MENU_STRUCTURES,
    component: MenuStructures,
  },
  {
    path: SEMESTER_MENUS,
    component: SemesterMenus,
  },
  {
    path: SEMESTER_MENUS + "/:id",
    component: SemesterMenu,
  },
  {
    path: UPCOMING_MEALS,
    component: UpcomingMeals,
  },
  {
    path: DIETARY_BOOKINGS,
    component: DietaryBookings,
  },
  {
    path: DIETARY_TOTALS,
    component: DietaryBookingsTotals,
  },
  {
    path: MEAL_BOOKINGS,
    component: MealBookings,
  },
  {
    path: MEAL_BOOKINGS_TOTALS,
    component: MealBookingsTotals,
  },
  {
    path: ORDERS,
    component: Orders,
  },
];

export default routes;
